// useGenerateAnnounceMutation — multi-create мутация для тула «Анонсы для соцсетей».
//
// КОНТРАКТ Idempotency-Key (двойная генерация, BP-010 §8.3):
//   - На каждый пользовательский submit генерируется НОВЫЙ UUID v4, КРОМЕ
//     повторного клика после отправки, не получившей ОТВЕТА ПРИЛОЖЕНИЯ (обрыв
//     связи, таймаут или 5xx) с тем же текстом и тем же набором режимов —
//     тогда ключ ПЕРЕИСПОЛЬЗУЕТСЯ: записи на сервере могли уже создаться, и
//     повторная отправка с тем же ключом получит их же, а не создаст дубли.
//   - Ключ ОДИН на весь батч (один POST создаёт по две записи на каждый
//     отмеченный режим — все под одним ключом, а не по ключу на режим/слот).
//   - Повтор тем же ключом за 24ч → backend вернёт тот же набор variants
//     (idempotent_replay:true) вместо создания новых генераций.
//   - Повторный клик после ПОЛУЧЕННОГО ответа сервера — это НОВЫЙ ключ и
//     новая платная генерация: редактор должен иметь возможность осознанно
//     перегенерировать, даже если ничего не поменял в тексте. От случайного
//     двойного клика защищает не ключ, а блокировка кнопки «Сгенерировать»
//     на время генерации (SocialAnnouncerPage.tsx, §8.6).
//
// Реализация: ключ генерируется ВНУТРИ mutationFn. С retry=0 (глобальная
// настройка App.tsx defaultOptions.mutations.retry=0) mutationFn вызывается
// ровно один раз на mutate() — это и есть гарантия «один UUID на один клик
// пользователя» (если он не подпадает под переиспользование выше).
//
// Память ключа неотвеченной отправки (см. `unanswered` ниже) — ВНЕ компонента
// (не useRef): переход на вкладку «История» того же инструмента (ToolLayout)
// размонтирует SocialAnnouncerPage вместе со всеми её хуками, включая ЭТОТ.
// useRef эту память бы не пережил — редактор, вернувшись со страницы «Истории»
// после обрыва связи, получил бы НОВЫЙ ключ и заплатил бы за генерацию дважды.
// Модульная переменная переживает размонтирование (правка итогового ревью
// r4 №1, тот же приём, что и announcerRunStore.ts) и, как и раньше, теряется
// только при полной перезагрузке страницы (принятое ограничение §8.6).
//
// mutationKey (правка r5 №1): TanStack Query хранит мутации в ОБЩЕМ на вкладку
// MutationCache (привязан к queryClient, а не к компоненту). Пока запрос
// запуска не получил ответа, соответствующая Mutation остаётся в кэше со
// статусом "pending" НЕЗАВИСИМО от того, жив ли компонент, который её начал
// (node_modules/@tanstack/query-core/src/mutation.ts: optionalRemove не
// удаляет мутацию из кэша, пока status==="pending", даже если наблюдателей
// уже нет). SocialAnnouncerPage.tsx читает `useIsMutating({ mutationKey })`
// вместо `generateMutation.isPending` — так кнопка «Сгенерировать» остаётся
// заблокированной и ПОСЛЕ ухода со страницы и возврата, пока ответ ещё в пути.
import { useMutation, useQueryClient } from "@tanstack/react-query";
import { getNormalized } from "@/api/client";
import {
  generateAnnounce,
  generateIdempotencyKey,
  type AnnouncerGeneratePayload,
  type AnnouncerGenerateResponse,
} from "@/api/announcer";
import { setAnnouncerRun } from "@/pages/tools/SocialAnnouncer/announcerRunStore";

export const ANNOUNCER_GENERATE_MUTATION_KEY = ["announcer", "generate"] as const;

/** Расширенный результат — включает использованный ключ (для отладки и для
 *  E2E-теста, проверяющего «один ключ на весь батч») и признак того, что ключ
 *  был переиспользован с прошлой неотвеченной отправки. */
export interface AnnouncerGenerateResultWithKey extends AnnouncerGenerateResponse {
  idempotencyKey: string;
  reusedKey: boolean;
}

/** Подпись запроса для сверки при переиспользовании ключа: ВЕСЬ отправляемый
 *  запрос — обрезанные поля и НАБОР режимов (отсортирован: порядок кликов по
 *  чекбоксам не важен, набор записей на сервере тот же). Бэкенд тело при
 *  реплее не сверяет (GenerateAnnouncementView.post: реплей через
 *  _idempotency_batch, тело повторного запроса не сравнивается) — сверка
 *  нужна фронту, чтобы не подставить чужой ключ под изменившийся текст. */
export function announcerRequestSignature(p: AnnouncerGeneratePayload): string {
  return JSON.stringify([p.headline, p.body, p.wishes, [...p.modes].sort()]);
}

// Ключ последней отправки, не получившей ответа ПРИЛОЖЕНИЯ: обрыв связи,
// таймаут (status 0) или 5xx (в т.ч. 502/503/504 прокси) — записи на сервере
// могли уже создаться. Модульная переменная, а не useRef — см. комментарий в
// шапке файла. Значение читается и пишется синхронно внутри mutationFn,
// ре-рендер по нему не нужен.
let unanswered: { key: string; signature: string } | null = null;

/** ТОЛЬКО для тестов: сбросить память ключа между кейсами. Модуль — синглтон,
 *  он живёт дольше одного теста (как и announcerRunStore.ts), поэтому без
 *  явного сброса в beforeEach тесты становятся зависимыми друг от друга.
 *  Импортировать только из *.test.tsx. */
export function resetAnnouncerUnansweredKeyForTests(): void {
  unanswered = null;
}

/** Сбросить ОБА клиентских синглтона анонсера — память ключа неотвеченной
 *  отправки (`unanswered`, этот файл) и сохранённый запуск (announcerRunStore.ts)
 *  — при выходе из аккаунта (низкая r5 №8). Вызывается из ЕДИНОЙ точки выхода
 *  `resetClientState` (`useAuth.ts`), которая уже отрабатывает и на обычный
 *  logout, и на logout из соседней вкладки (useCrossTabLogout.ts) — правка не
 *  меняет поведение самого выхода, только дополняет его для этого инструмента.
 *
 *  Фактического вреда без этой правки не было (см. r5_iteration2_review.md
 *  №8): страница фильтрует показ запуска по userId, а сервер ищет батч по
 *  идемпотентному ключу СРЕДИ ЗАПИСЕЙ КОНКРЕТНОГО user= (views.py), ключ не
 *  уникален глобально (models.py, db_index без unique) — чужой ключ или чужой
 *  запуск в той же вкладке не «утекли» бы следующему пользователю. Правка —
 *  для чистоты модели «выход = пустая вкладка», а не защита от инцидента. */
export function resetAnnouncerClientState(): void {
  unanswered = null;
  setAnnouncerRun(null);
}

export function useGenerateAnnounceMutation() {
  const qc = useQueryClient();

  return useMutation<AnnouncerGenerateResultWithKey, Error, AnnouncerGeneratePayload>({
    mutationKey: ANNOUNCER_GENERATE_MUTATION_KEY,
    mutationFn: async (payload) => {
      const signature = announcerRequestSignature(payload);
      const reusable = unanswered?.signature === signature ? unanswered.key : null;
      const idempotencyKey = reusable ?? generateIdempotencyKey();
      unanswered = { key: idempotencyKey, signature };
      try {
        const result = await generateAnnounce(payload, idempotencyKey);
        unanswered = null; // ответ сервера получен — ключ больше не нужен
        return { ...result, idempotencyKey, reusedKey: reusable !== null };
      } catch (err) {
        const status = getNormalized(err).status;
        // Сбрасываем ключ только на ответ приложения 4xx (валидация, доступ,
        // троттлинг): записей нет. При status 0 и 5xx ключ сохраняется —
        // реплей безопасен: сервер вернёт уже созданный батч или создаст
        // новый, если записей нет (GenerateAnnouncementView.post — реплей по
        // ключу идёт до валидации тела, backend/apps/generations/views.py).
        if (status !== 0 && status < 500) unanswered = null;
        throw err;
      }
    },
    onSuccess: () => {
      // Каждый вариант = отдельная succeeded-генерация → освежаем историю тула,
      // иначе вкладка «История» покажет устаревший список до истечения staleTime.
      qc.invalidateQueries({ queryKey: ["history", "social_announcer"] });
    },
  });
}
